// src/screens/sales/store/components/form/Form.tsx

import { SetStateAction } from 'react'
import { Button } from '../../../../../common/components/button/Button'
import { FileUpload } from '../../../../../common/components/fileUpload/FileUpload'
import { Input } from '../../../../../common/components/input/Input'
import { Textarea } from '../../../../../common/components/textarea/Textarea'
import { StoreInterface } from '../../../../../common/types/interfaces'
import classes from './Form.module.sass'
import { useForm } from './hooks/useForm'
import { GMapInput } from '../../../../../common/components/gMapInput/GMapInput'
import { Toggle } from '../../../../../common/components/toggle/Toggle'
import { Schedule } from '../../../../../common/components/schedule/Schedule'
import { StoreFormView } from '../../../../../common/types/enums'
import { Modal } from '../../../../../common/components/modal/Modal'
import { ZoneMapEditor } from '../../../../../common/components/zoneMapEditor/ZoneMapEditor'

type Props = {
  edit: boolean
  el?: StoreInterface
  data: StoreInterface
  setData: React.Dispatch<SetStateAction<StoreInterface>>
  getList: () => void
  setModalActive: React.Dispatch<SetStateAction<boolean>>
}

export const Form = ({ edit, data, setData, getList, setModalActive, el }: Props) => {
  const {
    files,
    pairs,
    setFiles,
    setPairs,
    formAction,
    inputHandler,
    setDirty,
    getValidationResult,
    disabled,
    gMapInputHandler,
    toggleAction,
    view,
    setView,
    deliveryTariffs,
    mapModalActive,
    setMapModalActive,
    editingDeliveryId,
    openZoneEditor,
    saveZone,
    removeZone,
    getZoneByDeliveryId,
  } = useForm(data, setData, edit, getList, setModalActive, el)

  // ❌ Есть ли хоть одна зона у точки
  const hasAnyZone = (data.deliveryZones?.length || 0) > 0

  return (
    <div className={classes.container}>
      <div className={classes.bookMarks}>
        <div
          className={view === StoreFormView.MAIN ? classes.titleActive : classes.title}
          onClick={() => setView(StoreFormView.MAIN)}
        >
          {data.id ? 'редактирование' : 'добавление'}
        </div>

        <div
          className={view === StoreFormView.DELIVERY ? classes.titleActive : classes.title}
          onClick={() => setView(StoreFormView.DELIVERY)}
        >
          доставка
        </div>
      </div>

      {view === StoreFormView.MAIN && (
        <div className={classes.groupFormContainer}>
          <GMapInput
            fieldName="address"
            placeHolder="Введите адрес и выберете из списка"
            data={data}
            setData={gMapInputHandler}
            required
          />
          <Input
            type={'text'}
            fieldName={'name'}
            value={data.name}
            placeHolder={'Название торговой точки'}
            onChangeHandler={inputHandler}
            itemValidationResult={getValidationResult('name')}
            onBlur={setDirty}
          />
          <Input
            type={'text'}
            fieldName={'phone_number'}
            value={data.phone_number}
            placeHolder={'Телефон торговой точки'}
            onChangeHandler={inputHandler}
            itemValidationResult={getValidationResult('phone_number')}
            onBlur={setDirty}
          />
          <Textarea
            type={'textarea'}
            fieldName={'description'}
            value={data.description}
            placeHolder="Описание торговой точки"
            rows={5}
            cols={20}
            onChangeHandler={inputHandler}
            itemValidationResult={getValidationResult('description')}
            onBlur={setDirty}
          />

          <Input
            label="Порядок сортировки"
            type={'text'}
            fieldName={'sortOrder'}
            value={data.sortOrder}
            placeHolder={'Порядок сортировки'}
            onChangeHandler={inputHandler}
            itemValidationResult={getValidationResult('sortOrder')}
            onBlur={setDirty}
          />

          <Schedule data={data} setData={setData} />

          <div className={classes.toggles}>
            <div className={classes.toggleContainer}>
              <Toggle
                id="isActive"
                onChange={() => toggleAction('isActive')}
                checked={data.isActive}
              />
              <div>Активна</div>
            </div>

            <div className={classes.toggleContainer}>
              <Toggle
                id="temporarilyDisabled"
                onChange={() => toggleAction('temporarilyDisabled')}
                checked={data.temporarilyDisabled}
              />
              <div>Временно не работает</div>
            </div>
          </div>

          <FileUpload
            length={3}
            min_length={1}
            extensions={['jpeg', 'png', 'jpg', 'webp']}
            files={files}
            setFiles={setFiles}
            pairs={pairs}
            setPairs={setPairs}
          />
        </div>
      )}

      {view === StoreFormView.DELIVERY && (
        <div className={classes.groupFormContainer}>
          <div className={classes.titleActive}>настройки доставки</div>

          <div className={classes.toggleContainer}>
            <Toggle
              id="withDelivery"
              onChange={() => toggleAction('withDelivery')}
              checked={data.withDelivery}
            />
            <div>Есть доставка</div>
          </div>

          {data.withDelivery && (
            <>
              {/* ❌ Предупреждение: если есть зоны — радиус точки и радиусы кластера не действуют */}
              {hasAnyZone && (
                <div className={classes.zoneWarning}>
                  ⚠️ У точки есть хотя бы одна зона. Радиус доставки точки и радиусы тарифов
                  кластера не действуют. Работают только тарифы с нарисованными зонами.
                </div>
              )}

              {!hasAnyZone && (
                <Input
                  type={'numberText'}
                  fieldName={'deliveryRadius'}
                  value={data.deliveryRadius || ''}
                  placeHolder={'Радиус доставки max, м'}
                  onChangeHandler={inputHandler}
                  itemValidationResult={getValidationResult('deliveryRadius')}
                  onBlur={setDirty}
                />
              )}

              {deliveryTariffs.length > 0 && (
                <div className={classes.zonesSection}>
                  <div className={classes.titleActive}>зоны доставки по тарифам</div>
                  {deliveryTariffs.map((tariff) => {
                    const zone = getZoneByDeliveryId(tariff.id!)
                    return (
                      <div key={tariff.id} className={classes.zoneItem}>
                        <div className={classes.zoneInfo}>
                          <div className={classes.zoneLabel}>
                            {tariff.tag ? `[${tariff.tag}] ` : ''}
                            {tariff.level ? `от ${tariff.level} ₽` : 'Базовый тариф'}
                          </div>
                          <div className={classes.zoneValue}>
                            {tariff.value} ₽ · исполнителю {tariff.fee || 0} ₽
                            {tariff.radius ? ` · радиус ${tariff.radius} м` : ''}
                          </div>
                        </div>
                        <div className={classes.zoneActions}>
                          {zone ? (
                            <>
                              <span className={classes.zoneStatus}>✅ Зона</span>
                              <button onClick={() => openZoneEditor(tariff.id!)}>Изменить</button>
                              <button onClick={() => removeZone(tariff.id!)}>Удалить</button>
                            </>
                          ) : (
                            <button onClick={() => openZoneEditor(tariff.id!)}>Зона</button>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </>
          )}
        </div>
      )}

      <Button text={edit ? 'Сохранить' : 'Добавить'} onClick={formAction} disabled={disabled} />

      {/* ❌ Модалка с картой */}
      {mapModalActive && data.address?.latitude && data.address?.longitude && (
        <Modal modalActive={mapModalActive} setModalActive={setMapModalActive}>
          <ZoneMapEditor
            center={{
              lat: Number(data.address.latitude),
              lng: Number(data.address.longitude),
            }}
            initialPolygon={
              editingDeliveryId ? getZoneByDeliveryId(editingDeliveryId)?.polygon : undefined
            }
            otherZones={(data.deliveryZones || [])
              .filter((z) => z.clusterDeliveryId !== editingDeliveryId)
              .map((z) => ({
                clusterDeliveryId: z.clusterDeliveryId,
                polygon: (z.polygon || []).map((p) => ({
                  lat: Number(p.lat),
                  lng: Number(p.lng),
                })),
              }))}
            onSave={saveZone}
            onClose={() => setMapModalActive(false)}
          />
        </Modal>
      )}
    </div>
  )
}
